import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { MetaGuard, AuthGuard } from '../setup/guards';

import Dashboard from '../views/Dashboard.vue';

const routes: Array<RouteRecordRaw> = [

	/* ============ MAIN ============ */
	{
		path: '/',
		name: 'Dashboard',
		meta: { title: 'Dashboard | Liberty Controller', public: false, loggedIn: true },
		component: Dashboard,
	},
	{
		path: '/reports/:instance_id?',
		name: 'Reports',
		meta: { title: 'Reports | Liberty Controller', public: false, singleTenant: true, admin: true, reporting: true },
		component: () => import('../views/Reports.vue'),
	},
	{
		path: '/audit',
		name: 'Audit',
		meta: { title: 'Audit | Liberty Controller', public: false, admin: true, reporting: true },
		component: () => import('../views/Audit.vue'),
	},
	{
		path: '/about',
		name: 'About',
		meta: { title: 'About | Liberty Controller', public: false, admin: true, reporting: true },
		component: () => import('../views/About.vue'),
	},
	{
		path: '/about/dependencies',
		name: 'Dependencies',
		meta: { title: 'Dependencies | Liberty Controller', public: false, admin: true },
		component: () => import('../views/Dependencies.vue'),
	},

	/* ============ LICENSING ============ */
	{
		path: '/licensing',
		name: 'Licensing',
		meta: { title: 'Licensing | Liberty Controller', public: false, admin: true, reporting: true },
		component: () => import('../views/licensing/Wrapper.vue'),
		children: [
			{
				path: 'user-licensing',
				name: 'Licensing - User Licensing',
				meta: { title: 'User Licensing | Licensing | Liberty Controller', public: false, admin: true, reporting: true },
				component: () => import('../views/licensing/UserLicensing.vue'),
			},
			{
				path: 'login-usage',
				name: 'Licensing - Usage',
				meta: { title: 'Usage | Licensing | Liberty Controller', public: false, admin: true },
				component: () => import('../views/licensing/LoginUsage.vue'),
			},
			{
				path: 'ai-unit-usage',
				name: 'Licensing - Ai Unit Usage',
				meta: { title: 'AI Unit Usage | Licensing | Liberty Controller', public: false, admin: true },
				component: () => import('../views/licensing/AiUnitUsage.vue'),
			},
			{
				path: 'idp-usage',
				name: 'Licensing - IDP Usage',
				meta: { title: 'IDP Usage | Licensing | Liberty Controller', public: false, admin: true },
				component: () => import('../views/licensing/IdpUsage.vue'),
			},
		],
	},

	/* ============ AUTHENTICATION ============ */
	{
		path: '/auth/login',
		name: 'Login',
		meta: { title: 'Login | Liberty Controller', public: true },
		component: () => import('../views/auth/Login.vue'),
	},
	{
		path: '/auth/login/token',
		name: 'Login Token',
		meta: { title: '2FA Login | Liberty Controller', public: true },
		component: () => import('../views/auth/Token.vue'),
	},
	{
		path: '/auth/setup/:user_id/:token',
		name: 'UserSetup',
		meta: { title: 'Setup Your Account | Liberty Controller', public: true },
		component: () => import('../views/auth/Reset.vue'),
	},
	{
		path: '/auth/reset/:user_id/:token',
		name: 'UserReset',
		meta: { title: 'Reset Your Account | Liberty Controller', public: true },
		component: () => import('../views/auth/Reset.vue'),
	},
	{
		path: '/auth/sso/authenticate',
		alias: '/auth/sso/authenticate/check',
		name: 'SsoAuthenticate',
		meta: { title: 'SSO Authenticate | Liberty Controller', public: true },
		component: () => import('../views/auth/SsoAuthenticate.vue'),
	},
	{
		// just in case user tries to access /auth/sso instead of /sso/login
		path: '/auth/sso/login',
		name: 'SsoInit',
		meta: { title: 'SSO Initialise | Liberty Controller', public: true },
		redirect: () => {
			window.location.assign('/sso/login');
			return '/';
		},
	},

	/* ============ CREATE ============ */
	{
		path: '/create/applications',
		name: 'Applications',
		meta: { title: 'Applications | Liberty Controller', public: false, create: true },
		component: () => import('../views/create/Applications.vue'),
	},
	{
		path: '/create/environments',
		name: 'Environments',
		meta: { title: 'Environments | Liberty Controller', public: false, create: true },
		component: () => import('../views/create/Environments.vue'),
	},
	{
		path: '/create/resources',
		name: 'Resources',
		meta: { title: 'Resources | Liberty Controller', public: false, admin: true },
		component: () => import('../views/create/Resources.vue'),
	},

	/* ============ CREATE - APPLICATION ============ */
	{
		path: '/create/applications/:application_id',
		name: 'Application Information',
		meta: { title: 'Application | Liberty Controller', public: false, create: true },
		component: () => import('../views/create/application/Wrapper.vue'),
		children: [
			{
				path: 'environments',
				name: 'Application - Environments',
				meta: { title: 'Environments - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/Environments.vue'),
			},
			{
				path: 'users',
				name: 'Application - Users',
				meta: { title: 'Users - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/Users.vue'),
			},
			{
				path: 'overview',
				name: 'Application - Overview',
				meta: { title: 'Overview - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/Overview.vue'),
			},
			{
				path: 'resources',
				name: 'Application - Resources',
				meta: { title: 'Resources - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/Resources.vue'),
			},
			{
				path: 'audit',
				name: 'Application - Audit',
				meta: { title: 'Audit - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/Audit.vue'),
			},
		],
	},

	/* ============ CREATE - ENVIRONMENT ============ */
	{
		path: '/create/application/:application_id/:environment_id',
		name: 'Application - Environment',
		meta: { title: 'Environment - Application | Liberty Controller', public: false, create: true },
		component: () => import('../views/create/application/environment/Wrapper.vue'),
		children: [
			{
				path: 'users',
				name: 'Environment - Approvers',
				meta: { title: 'Users - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Users.vue'),
			},
			{
				path: 'backups',
				name: 'Environment - Backups',
				meta: { title: 'Backups - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Backups.vue'),
			},
			{
				path: 'overview',
				name: 'Environment - Overview',
				meta: { title: 'Overview - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Overview.vue'),
			},
			{
				path: 'snapshots',
				name: 'Environment - Snapshots',
				meta: { title: 'Snapshots - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Snapshots.vue'),
			},
			{
				path: 'upgrade',
				name: 'Environment - Upgrade',
				meta: { title: 'Upgrade - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Upgrade.vue'),
			},
			{
				path: 'reports',
				name: 'Environment - Reports',
				meta: { title: 'Reports - Environment - Application | Liberty Controller', public: false, create: true, singleTenant: false },
				component: () => import('../views/create/application/environment/Reports.vue'),
			},
			{
				path: 'resources',
				name: 'Environment - Resources',
				meta: { title: 'Resources - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Resources.vue'),
			},
			{
				path: 'audit',
				name: 'Environment - Audit',
				meta: { title: 'Resources - Environment - Application | Liberty Controller', public: false, create: true },
				component: () => import('../views/create/application/environment/Audit.vue'),
			},
		],
	},

	/* ============ RPA ============ */
	{
		path: '/rpa/flows',
		name: 'Flows',
		meta: { title: 'Flows | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Flows.vue'),
	},
	{
		path: '/rpa/flow',
		name: 'Flow - Create',
		meta: { title: 'Create Flow | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Flow.vue'),
	},
	{
		path: '/rpa/flow/:flow_id',
		name: 'Flow - Edit',
		meta: { title: 'Edit Flow | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Flow.vue'),
	},
	{
		path: '/rpa/jobs',
		name: 'Jobs',
		meta: { title: 'Jobs | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Jobs.vue'),
	},
	{
		path: '/rpa/job',
		name: 'Job - Create',
		meta: { title: 'Create Job | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Job.vue'),
	},
	{
		path: '/rpa/job/:job_id',
		name: 'Job - View',
		meta: { title: 'View Job | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Job.vue'),
	},
	{
		path: '/rpa/job/:job_id',
		name: 'Job - Edit',
		meta: { title: 'Edit Job | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Job.vue'),
	},
	{
		path: '/rpa/bots',
		name: 'Bots',
		meta: { title: 'Bots | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Bots.vue'),
	},
	{
		path: '/rpa/bot/:bot_id',
		name: 'Bot - Edit',
		meta: { title: 'Edit Bot | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Bot.vue'),
	},
	{
		path: '/rpa/credentials',
		name: 'Credentials',
		meta: { title: 'Credentials | Liberty Controller', public: false, rpa: true },
		component: () => import('../views/rpa/Credentials.vue'),
	},

	/* ============= AI ============== */
	{
		path: '/ai/models',
		name: 'Models',
		meta: { title: 'Models | Liberty Controller', public: false, ai: true },
		component: () => import('../views/ai/Models.vue'),
	},
	{
		path: '/ai/model/:model_id',
		name: 'AI Model',
		meta: { title: 'AI | Liberty Controller', public: false, ai: true },
		component: () => import('../views/ai/Wrapper.vue'),
		children: [
			{
				path: 'basics',
				name: 'Model - Basics',
				meta: { title: 'Basic - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/model/Basics.vue'),
			},
			{
				path: 'configure',
				name: 'Model - Configure',
				meta: { title: 'Configure - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/model/Configure.vue'),
			},
			{
				path: 'train',
				name: 'Model - Train',
				meta: { title: 'Train - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/model/Train.vue'),
			},
			{
				path: 'review',
				name: 'Model - Review',
				meta: { title: 'Review - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/model/Review.vue'),
			},
			{
				path: 'test',
				name: 'Model - Test',
				meta: { title: 'Test - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import(/* webpackChunkName: "edit_ai_model_review" */ '../views/ai/model/Test.vue'),
			},
			{
				path: 'confusion-matrix/:training_id/full-page',
				name: 'Model - Confusion Matrix',
				meta: { title: 'Confusion Matrix - AI Model | Liberty Controller', public: false, ai: true },
				component: () => import(/* webpackChunkName: "edit_ai_model_review" */ '../views/ai/model/ConfusionMatrixFullPage.vue'),
			},
		],
	},
	{
		path: '/ai/knowledge-management',
		name: 'AI Knowledge Management',
		meta: { title: 'AI Knowledge Management', public: false, ai: true },
		component: () => import('../views/ai/knowledge_management/Wrapper.vue'),
		children: [
			{
				path: 'local',
				name: 'AI Knowledge Management - Local',
				meta: { title: 'AI Knowledge Management - local', public: false, ai: true },
				component: () => import('../views/ai/knowledge_management/local/Wrapper.vue'),
				children: [
					{
						path: 'collections',
						name: 'AI Knowledge Management - Local Collections',
						meta: { title: 'Local Collections - AI Knowledge Management | Liberty Controller', public: false, ai: true },
						component: () => import('../views/ai/knowledge_management/local/Collections.vue'),
					},
					{
						path: 'qa-pairs',
						name: 'AI Knowledge Management - QA Pairs',
						meta: { title: 'QA Pairs - AI Knowledge Management | Liberty Controller', public: false, ai: true },
						component: () => import('../views/ai/knowledge_management/local/QaPairs.vue'),
					},
					{
						path: 'web-crawls',
						name: 'AI Knowledge Management - Web Crawls',
						meta: { title: 'Web Crawls - AI Knowledge Management | Liberty Controller', public: false, ai: true },
						component: () => import('../views/ai/knowledge_management/local/WebCrawls.vue'),
					},
				],
			},
			{
				path: 'dynamic',
				name: 'AI Knowledge Management - Dynamic',
				meta: { title: 'AI Knowledge Management - dynamic', public: false, ai: true },
				component: () => import('../views/ai/knowledge_management/dynamic/Wrapper.vue'),
				children: [
					{
						path: 'groups',
						name: 'AI Knowledge Management - Dynamic Groups',
						meta: { title: 'Dynamic Groups - AI Knowledge Management | Liberty Controller', public: false, ai: true },
						component: () => import('../views/ai/knowledge_management/dynamic/Groups.vue'),
					},
					{
						path: 'group/:group_name_base64url_encoded.:app_id.:env_num/collections',
						name: 'AI Knowledge Management - Dynamic Group',
						meta: { title: 'Dynamic Group - AI Knowledge Management | Liberty Controller', public: false, ai: true },
						component: () => import('../views/ai/knowledge_management/dynamic/Group.vue'),
					},
				],
			},
		],
	},
	{
		path: '/ai/knowledge-management/local/collection/:collection_id',
		name: 'AI Knowledge Management - Local Collection',
		meta: { title: 'Local Collection - AI Knowledge Management | Liberty Controller', public: false, ai: true },
		component: () => import('../views/ai/knowledge_management/local/collection/Wrapper.vue'),
		children: [
			{
				path: 'documents',
				name: 'AI Knowledge Management - Local Collection - Documents',
				meta: { title: 'Documents - Local Collection - AI Knowledge Management | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/knowledge_management/local/collection/Documents.vue'),
			},
			{
				path: 'qa-pairs',
				name: 'AI Knowledge Management - Local Collection - QA Pairs',
				meta: { title: 'QA Pairs - Local Collection - AI Knowledge Management | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/knowledge_management/local/collection/QaPairs.vue'),
			},
			{
				path: 'web-crawls',
				name: 'AI Knowledge Management - Local Collection - Web Crawls',
				meta: { title: 'Web Crawls - Local Collection - AI Knowledge Management | Liberty Controller', public: false, ai: true },
				component: () => import('../views/ai/knowledge_management/local/collection/WebCrawls.vue'),
			},
		],
	},

	/* ======== Converse CX ========== */
	{
		path: '/converse-cx/dashboard',
		name: 'Converse CX - Dashboard',
		meta: { title: 'Dashboard - Converse CX | Liberty Controller', public: false, converse: true },
		component: () => import('../views/converse_cx/Dashboard.vue'),
	},

	/* ======== Spark ========== */
	{
		path: '/spark/dashboard',
		name: 'Spark - Dashboard',
		meta: { title: 'Dashboard - Spark | Liberty Controller', public: false, spark: true },
		component: () => import('../views/spark/Dashboard.vue'),
	},

	/* ======== IDP ========== */
	{
		path: '/idp/document-types',
		name: 'IDP - Document Types',
		meta: { title: 'Document Types - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/DocumentTypes.vue'),
	},
	{
		path: '/idp/document-type/:document_type_id',
		name: 'IDP - Document Type',
		meta: { title: 'Document Type - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/document_type/Wrapper.vue'),
		children: [
			{
				path: 'basics',
				name: 'IDP - Document Type - Basics',
				meta: { title: 'Basics - Document Type - IDP | Liberty Controller', public: false, idp: true },
				component: () => import('../views/idp/document_type/Basics.vue'),
			},
			{
				path: 'fields',
				name: 'IDP - Document Type - Fields',
				meta: { title: 'Fields - Document Type - IDP | Liberty Controller', public: false, idp: true },
				component: () => import('../views/idp/document_type/Fields.vue'),
			},
			{
				path: 'prompts',
				name: 'IDP - Document Type - Prompts',
				meta: { title: 'Prompts - Document Type - IDP | Liberty Controller', public: false, idp: true },
				component: () => import('../views/idp/document_type/Prompts.vue'),
			},
			{
				path: 'review',
				name: 'IDP - Document Type - Review',
				meta: { title: 'Review - Document Type - IDP | Liberty Controller', public: false, idp: true },
				component: () => import('../views/idp/document_type/Review.vue'),
			},
			{
				path: 'tests/:test_id/preview',
				name: 'IDP - Document Type - Test Results',
				meta: { title: 'Test Results - Document Type - IDP | Liberty Controller', public: false, idp: true },
				component: () => import('../views/idp/document_type/TestResults.vue'),
			},
		],
	},

	{
		path: '/idp/bundles',
		name: 'IDP - Bundles',
		meta: { title: 'Bundles - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/Bundles.vue'),
	},
	{
		path: '/idp/bundle',
		name: 'IDP - Bundle - Create',
		meta: { title: 'Create Bundle - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/bundle/Bundle.vue'),
	},
	{
		path: '/idp/bundle/:bundle_id',
		name: 'IDP - Bundle - Edit',
		meta: { title: 'Edit Bundle - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/bundle/Bundle.vue'),
	},
	{
		path: '/idp/bundle/:bundle_id/tests/:test_id/preview',
		name: 'IDP - Bundle - Test Results',
		meta: { title: 'Test Results - Bundle - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/bundle/TestResults.vue'),
	},

	{
		path: '/idp/api-and-ui-access',
		name: 'IDP - API and UI Access',
		meta: { title: 'API and UI Access - IDP | Liberty Controller', public: false, idp: true },
		component: () => import('../views/idp/ApiAndUiAccess.vue'),
	},

	/* ============ USERS ============ */
	{
		path: '/users',
		name: 'UsersList',
		meta: { title: 'Users | Liberty Controller', public: false, admin: true, reporting: true },
		component: () => import('../views/users/List.vue'),
	},
	{
		path: '/users/create',
		name: 'UserCreate',
		meta: { title: 'Create User | Liberty Controller', public: false, admin: true },
		component: () => import('../views/users/Create.vue'),
	},
	{
		path: '/users/edit/:user_id',
		name: 'UserEdit',
		meta: { title: 'Edit User | Liberty Controller', public: false, admin: true },
		component: () => import('../views/users/Edit.vue'),
	},

	/* ============ SETTINGS ============ */
	{
		path: '/settings',
		//	redirect: '/settings/authentication',
		name: 'Settings',
		meta: { title: 'Settings | Liberty Controller', public: false, admin: true },
		component: () => import('../views/settings/Wrapper.vue'),
		children: [
			{
				path: 'authentication',
				name: 'Settings - Authentication',
				meta: { title: 'Authentication - Settings | Liberty Controller', public: false, admin: true },
				component: () => import('../views/settings/Authentication.vue'),
			},
			{
				path: 'custom_domains',
				name: 'Settings - Custom Domains',
				meta: { title: 'Custom Domains - Settings | Liberty Controller', public: false, admin: true },
				component: () => import('../views/settings/CustomDomains.vue'),
			},
			{
				path: 'firewall',
				name: 'Settings - Firewall',
				meta: { title: 'Firewall - Settings | Liberty Controller', public: false, admin: true },
				component: () => import('../views/settings/Firewall.vue'),
			},
			{
				path: 'intrusion_prevention',
				name: 'Settings - Intrusion Prevention',
				meta: { title: 'Intrusion Prevention - Settings | Liberty Controller', public: false, admin: true },
				component: () => import('../views/settings/IntrusionPrevention.vue'),
			},
		],
	},

	/* ============ ERROR ============ */
	{
		path: '/not-found',
		name: 'NotFound',
		meta: { title: 'Not Found | Liberty Controller', public: false, admin: true, rpa: true, create: true, ai: true },
		component: () => import('../views/NotFound.vue'),
	},
	{
		path: '/page404',
		name: '404',
		meta: { title: 'Not Found | Liberty Controller', public: true },
		component: () => import('../views/errors/Page404.vue'),
	},
	{
		path: '/forbidden',
		name: '403',
		meta: { title: 'Forbidden | Liberty Controller', public: true },
		component: () => import('../views/errors/Page403.vue'),
	},
	{
		path: '/:catchAll(.*)*',
		name: 'NotFound',
		meta: { title: 'Not Found | Liberty Controller', public: false, admin: true, rpa: true, create: true, ai: true },
		component: () => import('../views/NotFound.vue'),
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes,
});

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
	MetaGuard(to, from, next);
});

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
	AuthGuard(to, from, next);
});

export default router;
